/**
 * Onboarding Store
 *
 * Manages role-specific onboarding flow state.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  OnboardingFlowType,
  OnboardingProgress,
  GuidedTourStop,
} from '../types'
import { onboardingApi } from '../api'

interface OnboardingState {
  flowType: OnboardingFlowType | null
  currentStep: number
  totalSteps: number
  completedSteps: string[]
  stepData: Record<string, unknown>
  completedAt: string | null
  tourStops: GuidedTourStop[]
  isTourActive: boolean
  currentTourStop: number

  isLoading: boolean
  error: string | null

  // Actions
  initFlow: (flowType: OnboardingFlowType, totalSteps: number) => Promise<void>
  nextStep: (stepId: string, data?: Record<string, unknown>) => Promise<void>
  prevStep: () => void
  completeFlow: () => Promise<void>
  loadExistingProgress: (flowType: OnboardingFlowType) => Promise<boolean>
  startTour: (role: string) => Promise<void>
  nextTourStop: () => void
  endTour: () => void
  reset: () => void
}

export const useOnboardingStore = createWithEqualityFn<OnboardingState>((set, get) => ({
  flowType: null,
  currentStep: 0,
  totalSteps: 0,
  completedSteps: [],
  stepData: {},
  completedAt: null,
  tourStops: [],
  isTourActive: false,
  currentTourStop: 0,
  isLoading: false,
  error: null,

  initFlow: async (flowType, totalSteps) => {
    set({ isLoading: true, error: null })
    const response = await onboardingApi.initFlow(flowType, totalSteps)
    if (response.success && response.data) {
      const progress = response.data
      set({
        flowType,
        currentStep: progress.currentStep,
        totalSteps: progress.totalSteps,
        completedSteps: progress.completedSteps,
        stepData: progress.data ?? {},
        completedAt: progress.completedAt ?? null,
        isLoading: false,
      })
    } else {
      set({ error: response.error ?? 'Failed to init onboarding', isLoading: false })
    }
  },

  nextStep: async (stepId, data = {}) => {
    const { flowType, currentStep } = get()
    if (!flowType) return

    set({ isLoading: true })
    const response = await onboardingApi.updateStep(flowType, currentStep + 1, stepId, data)
    if (response.success && response.data) {
      const progress = response.data
      set({
        currentStep: progress.currentStep,
        completedSteps: progress.completedSteps,
        stepData: progress.data ?? {},
        isLoading: false,
      })
    } else {
      set({ error: response.error ?? 'Failed to update step', isLoading: false })
    }
  },

  prevStep: () => {
    const { currentStep } = get()
    if (currentStep > 0) {
      set({ currentStep: currentStep - 1 })
    }
  },

  completeFlow: async () => {
    const { flowType } = get()
    if (!flowType) return

    set({ isLoading: true })
    const response = await onboardingApi.completeFlow(flowType)
    if (response.success && response.data) {
      set({ completedAt: response.data.completedAt ?? null, isLoading: false })
    } else {
      set({ error: response.error ?? 'Failed to complete flow', isLoading: false })
    }
  },

  loadExistingProgress: async (flowType) => {
    set({ isLoading: true, error: null })
    const response = await onboardingApi.getOnboardingState(flowType)
    if (response.success && response.data) {
      const progress = response.data as OnboardingProgress
      set({
        flowType,
        currentStep: progress.currentStep,
        totalSteps: progress.totalSteps,
        completedSteps: progress.completedSteps,
        stepData: progress.data ?? {},
        completedAt: progress.completedAt ?? null,
        isLoading: false,
      })
      return true
    }
    set({ isLoading: false })
    return false
  },

  startTour: async (role) => {
    const response = await onboardingApi.getGuidedTourStops(role)
    if (response.success && response.data) {
      set({
        tourStops: response.data,
        isTourActive: response.data.length > 0,
        currentTourStop: 0,
      })
    }
  },

  nextTourStop: () => {
    const { currentTourStop, tourStops } = get()
    if (currentTourStop < tourStops.length - 1) {
      set({ currentTourStop: currentTourStop + 1 })
    } else {
      set({ isTourActive: false, currentTourStop: 0 })
    }
  },

  endTour: () => {
    set({ isTourActive: false, currentTourStop: 0 })
  },

  reset: () => {
    set({
      flowType: null,
      currentStep: 0,
      totalSteps: 0,
      completedSteps: [],
      stepData: {},
      completedAt: null,
      tourStops: [],
      isTourActive: false,
      currentTourStop: 0,
      isLoading: false,
      error: null,
    })
  },
}))

// Selectors
export const selectOnboardingStep = (state: OnboardingState) => state.currentStep
export const selectIsOnboardingComplete = (state: OnboardingState) => state.completedAt !== null
export const selectTourActive = (state: OnboardingState) => state.isTourActive
